import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Flag, MapPin, Users, Award, Sun } from 'lucide-react';
import SEO from '../components/SEO';

const GolfIleDeRe = () => {
  return (
    <>
      <SEO 
        title="Golf Île de Ré | Golf de Trousse-Chemise - Séjour Golfeur"
        description="Séjour golf à l'Île de Ré : Golf de Trousse-Chemise 9 trous, parcours links en bord de mer. Villa donnant directement sur le golf, accès à pied par le jardin, pour vos vacances golf inoubliables."
        keywords="golf Île de Ré, golf Trousse-Chemise, séjour golfeur Ré, parcours golf Charente-Maritime, location villa golf Ré"
        ogImage="https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1200"
        url="/golf-ile-de-re"
      />

      {/* Hero */}
      <div className="relative h-[60vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/seo/golf-v2.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/70"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-white px-4">
          <div className="text-center max-w-4xl">
            <Flag className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Golf à l'Île de Ré
            </h1>
            <p className="text-lg sm:text-xl text-gray-200">
              Parcours d'exception face à l'océan - Séjour golfeur à la Villa Nomade
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Introduction */}
        <section className="mb-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">L'Île de Ré, Destination Golf d'Exception</h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto mb-8">
            Combinez votre passion pour le golf avec les charmes de l'<strong>Île de Ré</strong>. 
            Le <strong>Golf de Trousse-Chemise</strong>, parcours links 9 trous en bord de mer, 
            offre un cadre exceptionnel pour les golfeurs de tous niveaux. Notre <strong>Villa Nomade</strong>, 
            située directement en bordure du golf (accès à pied par le jardin), est l'hébergement idéal pour votre séjour golfeur.
          </p>
        </section>

        {/* Golf de Trousse-Chemise */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Golf de Trousse-Chemise</h2>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-lg shadow-lg mb-8">
            <div className="flex items-start gap-4 mb-6">
              <Award className="w-12 h-12 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="text-2xl font-bold mb-2">Parcours Links 9 Trous - Par 35</h3>
                <p className="text-gray-700">
                  Dessiné par l'architecte américain Bill Coore, ce parcours links de 2 800 mètres 
                  s'étend le long de la côte atlantique. Vue panoramique sur l'océan, bunkers stratégiques, 
                  greens ondulés : un défi technique dans un cadre naturel préservé.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white p-6 rounded-lg">
                <div className="text-3xl font-bold text-green-600 mb-2">9</div>
                <div className="text-sm text-gray-600">Trous - Par 35</div>
              </div>
              <div className="bg-white p-6 rounded-lg">
                <div className="text-3xl font-bold text-green-600 mb-2">2 800m</div>
                <div className="text-sm text-gray-600">Longueur totale</div>
              </div>
              <div className="bg-white p-6 rounded-lg">
                <div className="text-3xl font-bold text-green-600 mb-2">0 km</div>
                <div className="text-sm text-gray-600">En bordure directe</div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Flag className="w-6 h-6 text-[#c17a3a]" />
                Caractéristiques du Parcours
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ <strong>Type :</strong> Links authentique</li>
                <li>✓ <strong>Style :</strong> Tracé naturel dans les dunes</li>
                <li>✓ <strong>Difficulté :</strong> Tous niveaux (5 départs différents)</li>
                <li>✓ <strong>Particularité :</strong> Vent océanique constant</li>
                <li>✓ <strong>Fairways :</strong> Ondulés, jeu au sol privilégié</li>
                <li>✓ <strong>Greens :</strong> Rapides et techniques</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Users className="w-6 h-6 text-[#c17a3a]" />
                Services & Équipements
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Club-house avec restaurant panoramique</li>
                <li>✓ Pro-shop (matériel, vêtements)</li>
                <li>✓ Practice couvert & en extérieur</li>
                <li>✓ Putting green & pitching green</li>
                <li>✓ Location de clubs & chariots électriques</li>
                <li>✓ Cours particuliers & stages</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Tarifs */}
        <section className="mb-16 bg-gray-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Tarifs Golf (2026)</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="text-sm text-gray-600 mb-2">GREEN FEE 9 TROUS</div>
              <div className="text-3xl font-bold text-gray-900 mb-2">60€</div>
              <div className="text-sm text-gray-600">Haute saison (Juil-Août)</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="text-sm text-gray-600 mb-2">GREEN FEE 9 TROUS</div>
              <div className="text-3xl font-bold text-gray-900 mb-2">45€</div>
              <div className="text-sm text-gray-600">Moyenne saison</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="text-sm text-gray-600 mb-2">LOCATION CLUBS</div>
              <div className="text-3xl font-bold text-gray-900 mb-2">25€</div>
              <div className="text-sm text-gray-600">Série complète</div>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-6">
            Réservation en ligne recommandée : <a href="https://www.golf-iledere.com" target="_blank" rel="noopener" className="text-[#c17a3a] hover:underline">www.golf-iledere.com</a>
          </p>
        </section>

        {/* Villa pour golfeurs */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Villa Nomade - Votre Hébergement Golf</h2>
          <div className="bg-gradient-to-r from-amber-100 to-orange-100 p-8 rounded-lg">
            <div className="flex items-start gap-4 mb-6">
              <MapPin className="w-12 h-12 text-[#c17a3a] flex-shrink-0" />
              <div>
                <h3 className="text-2xl font-bold mb-3">En Bordure du Golf - Accès à Pied par le Jardin</h3>
                <p className="text-gray-700 mb-4">
                  Notre villa est l'hébergement idéal pour votre séjour golf. Après votre partie, 
                  détendez-vous dans le spa de nage chauffé, profitez du jardin au calme, 
                  et préparez votre prochain round dans le confort d'une villa haut de gamme.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div>
                <h4 className="font-semibold mb-3">🏌️ Parfait pour les golfeurs</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Garage fermé pour ranger votre matériel</li>
                  <li>• Spa de nage pour récupération musculaire</li>
                  <li>• Terrasses pour analyser votre partie</li>
                  <li>• BBQ pour soirées conviviales</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-3">👥 Groupes de golfeurs</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Accueil jusqu'à 8 personnes (4 chambres)</li>
                  <li>• Grande table 10 personnes</li>
                  <li>• Cuisine équipée pour repas de groupe</li>
                  <li>• Salon spacieux pour moments conviviaux</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Autres activités */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Entre Deux Rounds</h2>
          <p className="text-gray-700 mb-6">
            L'Île de Ré offre bien plus que le golf ! Profitez de votre séjour pour découvrir :
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Sun className="w-10 h-10 text-yellow-500 mb-3" />
              <h3 className="font-semibold mb-2">Plages exceptionnelles</h3>
              <p className="text-gray-700 text-sm">Détente et baignade après le golf</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Users className="w-10 h-10 text-blue-500 mb-3" />
              <h3 className="font-semibold mb-2">Villages pittoresques</h3>
              <p className="text-gray-700 text-sm">Saint-Martin, Ars-en-Ré, La Flotte</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Flag className="w-10 h-10 text-green-500 mb-3" />
              <h3 className="font-semibold mb-2">Gastronomie locale</h3>
              <p className="text-gray-700 text-sm">Huîtres, fruits de mer, vins régionaux</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-[#c17a3a] to-[#a66830] text-white p-12 rounded-lg text-center">
          <h2 className="text-3xl font-bold mb-4">Réservez Votre Séjour Golf</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Villa en bordure directe du golf (accès à pied) • Spa de nage • Confort premium
            <br/>Le point de chute idéal pour vos vacances golfeur
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#c17a3a] hover:bg-gray-100">
                Réserver la villa
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Découvrir la villa
              </Button>
            </Link>
          </div>
        </section>
      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SportsActivityLocation",
          "name": "Golf de Trousse-Chemise",
          "description": "Parcours links 9 trous en bord de mer sur l'Île de Ré",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Les Portes-en-Ré",
            "addressRegion": "Charente-Maritime",
            "postalCode": "17880",
            "addressCountry": "FR"
          }
        })}
      </script>
    </>
  );
};

export default GolfIleDeRe;